import React from 'react';
import { useSEO } from '../hooks/useSEO';
import { useBanner } from '../contexts/BannerContext';

export const TermsPage: React.FC = () => {
  useSEO('terms' as any);
  const { isBannerVisible, bannerHeight } = useBanner();

  return (
    <main 
      className="pb-20"
      style={{ 
        paddingTop: isBannerVisible ? `${bannerHeight + 100}px` : '100px'
      }}
    >
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <header className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
              Terms & Conditions
            </h1>
            <p className="text-gray-700 dark:text-gray-300 text-lg">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </header>

          <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
            <div className="prose prose-invert max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  By accessing and using Cognitive Craft Hub's services, you accept and agree to be bound 
                  by the terms and provision of this agreement. If you do not agree to abide by the above, 
                  please do not use this service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">2. Services and Products</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Cognitive Craft Hub provides AI-powered digital solutions and healthcare technology products including but not limited to:
                </p>
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">AI Services:</h3>
                  <ul className="text-gray-700 dark:text-gray-300 list-disc ml-6 mb-4 space-y-2">
                    <li>Digital marketing automation (Cognitive Reach)</li>
                    <li>AI training and consulting services (Cognitive Train)</li>
                    <li>Cloud infrastructure solutions (Cognitive Cloud)</li>
                    <li>Custom software development (Cognitive Build)</li>
                    <li>AI and data analytics tools (Cognitive Intelligence)</li>
                    <li>AI automation and RPA solutions (Cognitive Agents)</li>
                    <li>Generative AI and content creation (Cognitive Generate)</li>
                    <li>Computer vision and image analytics (Cognitive Vision)</li>
                    <li>Conversational AI and voice solutions (Cognitive Voice)</li>
                    <li>Predictive analytics and forecasting (Cognitive Predict)</li>
                    <li>Internet of Things implementations (Cognitive IoT)</li>
                    <li>Advanced cybersecurity solutions (Cognitive Secure)</li>
                    <li>Robotics implementation and automation consulting (Cognitive Robotics)</li>
                    <li>Regulatory and legal AI solutions (Cognitive Compliance)</li>
                    <li>Blockchain and distributed ledger technology (Cognitive Ledger)</li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">3. Use License</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Permission is granted to temporarily download one copy of the materials on Cognitive Craft Hub's 
                  website for personal, non-commercial transitory viewing only. This is the grant of a license, 
                  not a transfer of title, and under this license you may not:
                </p>
                <ul className="text-gray-700 dark:text-gray-300 list-disc ml-6 mb-4 space-y-2">
                  <li>Modify or copy the materials</li>
                  <li>Use the materials for any commercial purpose or for any public display</li>
                  <li>Attempt to reverse engineer any software contained on the website</li>
                  <li>Remove any copyright or other proprietary notations from the materials</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">4. General Disclaimer</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  The materials on Cognitive Craft Hub's website are provided on an 'as is' basis. Cognitive Craft Hub 
                  makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties 
                  including without limitation, implied warranties or conditions of merchantability, fitness for 
                  a particular purpose, or non-infringement of intellectual property or other violation of rights.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">5. Limitations</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  In no event shall Cognitive Craft Hub or its suppliers be liable for any damages (including, 
                  without limitation, damages for loss of data or profit, or due to business interruption) 
                  arising out of the use or inability to use the materials on Cognitive Craft Hub's website, 
                  even if Cognitive Craft Hub or an authorized representative has been notified orally or 
                  in writing of the possibility of such damage.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">6. Privacy Policy</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect 
                  your information when you use our services. By using our services, you agree to the collection 
                  and use of information in accordance with our Privacy Policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">7. Governing Law</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  These terms and conditions are governed by and construed in accordance with the laws of 
                  Michigan, United States, and you irrevocably submit to the exclusive jurisdiction of the 
                  courts in that State or location.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">8. Changes to Terms</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Cognitive Craft Hub reserves the right to modify these terms at any time. We will notify
                  users of any material changes by posting the new terms on this page. Your continued use
                  of our services after such modifications will constitute your acknowledgment of the modified
                  terms and agreement to abide by and be bound by the modified terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">9. Third-Party Products and Services</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Cognitive Craft Hub acts as a consultant, systems integrator, and implementation partner for
                  various technology solutions including but not limited to robotics automation systems. We do
                  not manufacture robotics hardware, software, or other third-party products.
                </p>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  All third-party equipment, hardware, and software are subject to the respective manufacturer's
                  warranties, terms of service, and support agreements. Cognitive Craft Hub is not liable for:
                </p>
                <ul className="text-gray-700 dark:text-gray-300 list-disc ml-6 mb-4 space-y-2">
                  <li>Defects in third-party manufactured equipment or software</li>
                  <li>Performance issues or malfunctions of third-party products</li>
                  <li>Warranty claims related to manufacturer equipment</li>
                  <li>Third-party vendor service interruptions or discontinuation</li>
                  <li>Compatibility issues between third-party products and client systems</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Our role is limited to vendor selection guidance, system integration, implementation support,
                  and consulting services. Clients acknowledge that final purchasing decisions and vendor
                  agreements are made independently.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">10. Robotics Safety and Operational Responsibility</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  For clients implementing robotics automation systems, the following safety and operational
                  responsibilities apply:
                </p>
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Client Responsibilities:</h3>
                  <ul className="text-gray-700 dark:text-gray-300 list-disc ml-6 mb-4 space-y-2">
                    <li>Proper operation and maintenance of robotics equipment according to manufacturer specifications</li>
                    <li>Compliance with OSHA and all applicable workplace safety regulations</li>
                    <li>Implementation of appropriate safety protocols and emergency shutdown procedures</li>
                    <li>Ensuring adequate staff training on safe robotics operation and maintenance</li>
                    <li>Conducting regular safety inspections and preventive maintenance</li>
                    <li>Maintaining appropriate insurance coverage for robotics operations</li>
                    <li>Compliance with local, state, and federal regulations governing robotics and automation</li>
                  </ul>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Cognitive Craft Hub provides implementation services, training, and technical guidance but is
                  not liable for injuries to persons, property damage, or operational incidents resulting from
                  robotics operation, misuse, lack of maintenance, or failure to follow safety protocols. Clients
                  assume all risk and responsibility for day-to-day robotics operations.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">11. Healthcare and Regulated Industry Compliance</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  For clients in healthcare, medical facilities, or other regulated industries implementing AI,
                  robotics, or technology solutions:
                </p>
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Healthcare-Specific Requirements:</h3>
                  <ul className="text-gray-700 dark:text-gray-300 list-disc ml-6 mb-4 space-y-2">
                    <li>Clients are solely responsible for HIPAA, HITECH Act, and state healthcare privacy law compliance</li>
                    <li>All robotics and AI implementations in healthcare settings must meet FDA regulations where applicable</li>
                    <li>Clients must ensure technology deployments comply with CMS requirements and Joint Commission standards</li>
                    <li>Healthcare organizations are responsible for conducting required risk assessments and impact analyses</li>
                    <li>Clients must obtain all necessary approvals from their compliance, legal, and risk management teams</li>
                  </ul>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Cognitive Craft Hub provides technical implementation and integration services with industry
                  best practices guidance. However, final compliance responsibility rests with the client
                  organization. We strongly recommend clients consult with legal counsel and compliance specialists
                  before implementing technology in regulated environments.
                </p>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Cognitive Craft Hub makes no warranties regarding regulatory compliance and is not liable for
                  regulatory violations, fines, or sanctions resulting from technology implementations.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">12. Performance Claims and Results Disclaimer</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Our website, marketing materials, and service descriptions may reference performance metrics,
                  cost savings estimates, ROI projections, or efficiency improvements (such as "30-50% cost reduction"
                  or "maximum ROI"). These figures are:
                </p>
                <ul className="text-gray-700 dark:text-gray-300 list-disc ml-6 mb-4 space-y-2">
                  <li>Based on industry averages and historical case studies</li>
                  <li>Estimates only and not guaranteed results</li>
                  <li>Subject to variation based on implementation scope, industry, client operations, and external factors</li>
                  <li>Dependent on client adherence to recommended practices and system usage</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  <strong>NO GUARANTEE OF RESULTS:</strong> Cognitive Craft Hub does not guarantee specific financial
                  results, cost savings, revenue increases, productivity improvements, or any other measurable outcomes.
                  Actual results will vary based on numerous factors including but not limited to: client industry,
                  operational complexity, staff adoption, market conditions, and implementation quality.
                </p>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Past performance of similar implementations does not guarantee future results. Clients should conduct
                  their own due diligence and financial analysis before making implementation decisions.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">13. Service Level Agreements and Support Terms</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  References to "24/7 support," "ongoing maintenance," or similar support commitments on our website
                  are general descriptions of available services. Specific service levels, response times, resolution
                  times, and support scope are defined in individual service agreements or statements of work.
                </p>
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Support Services:</h3>
                  <ul className="text-gray-700 dark:text-gray-300 list-disc ml-6 mb-4 space-y-2">
                    <li>Support availability and response times vary by service tier and agreement</li>
                    <li>Emergency support may require additional fees or service tier upgrades</li>
                    <li>Support is limited to services and systems implemented by Cognitive Craft Hub</li>
                    <li>Third-party product support is provided by respective manufacturers</li>
                    <li>Support services may be modified with reasonable notice to clients</li>
                  </ul>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Clients should review their specific service agreements for detailed support terms, limitations,
                  and exclusions. General website descriptions do not constitute binding service level commitments.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">14. Professional Services and Consulting Limitations</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Cognitive Craft Hub provides professional consulting, implementation, and technical services based
                  on industry best practices, technical expertise, and professional judgment. Our services are
                  advisory in nature and clients retain final decision-making authority.
                </p>
                <ul className="text-gray-700 dark:text-gray-300 list-disc ml-6 mb-4 space-y-2">
                  <li>Recommendations are based on information provided by clients and current industry standards</li>
                  <li>Clients are responsible for verifying all implementations meet their specific requirements</li>
                  <li>Technology recommendations reflect best judgment but may not be suitable for all situations</li>
                  <li>Implementation timelines are estimates and subject to change based on project complexity</li>
                  <li>Clients should conduct independent verification of critical system requirements</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  We recommend clients obtain appropriate professional liability insurance and consult with legal,
                  financial, and compliance advisors before making significant technology investments.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">15. Contact Information</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  If you have any questions about these Terms & Conditions, please contact us at:
                </p>
                <div className="bg-gray-100 dark:bg-black/20 p-4 rounded-lg">
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong className="text-gray-900 dark:text-white">Cognitive Craft Hub</strong><br />
                    Offices: East Lansing, Michigan & Houston, Texas<br />
                    Email: team@cognitivecrafthub.com<br />
                    Phone: <a href="tel:+15172025360" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">+1 (517) 202-5360</a>
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};